import type { ErrorEvent, EventHint } from '@sentry/core'
import { isStaleChunkReloadPending } from '@/lib/reloadOnStaleChunk'
import { httpRouteOf } from '@/lib/sentry-http-route'

const MAX_BODY_CHARS = 4000

function safeSerialize(value: unknown, maxLen: number): string {
  try {
    if (typeof value === 'string') {
      return value.length > maxLen ? `${value.slice(0, maxLen)}…` : value
    }
    const s = JSON.stringify(value)
    return s.length > maxLen ? `${s.slice(0, maxLen)}…` : s
  } catch {
    return '[unserializable]'
  }
}

function pickResponseHeaders(headers?: Headers): Record<string, string> {
  if (!headers || typeof headers.get !== 'function') return {}
  const out: Record<string, string> = {}
  const keys = [
    'content-type',
    'x-request-id',
    'x-correlation-id',
    'x-trace-id',
    'cf-ray',
  ] as const
  for (const k of keys) {
    const v = headers.get(k)
    if (v) out[k] = v
  }
  return out
}

/** oazapfts `HttpError`: message `Error: <status>`, numeric `status`, optional `data` / `headers`. */
export function isLikelyOazapftsHttpError(
  exception: unknown
): exception is Error & { status: number; data: unknown; headers?: Headers } {
  if (!exception || typeof exception !== 'object') return false
  if (!(exception instanceof Error)) return false
  const e = exception as Error & { status?: unknown; data?: unknown }
  if (typeof e.status !== 'number' || e.status < 400 || e.status > 599) {
    return false
  }
  return /^Error: \d{3}$/.test(e.message)
}

export function applyHttpErrorContextToEvent(
  event: ErrorEvent,
  hint: EventHint
): void {
  const ex = hint.originalException
  if (!isLikelyOazapftsHttpError(ex)) return

  event.extra = {
    ...event.extra,
    http_error_status: ex.status,
    http_error_body: safeSerialize(ex.data, MAX_BODY_CHARS),
  }

  const hdrs = pickResponseHeaders(ex.headers)
  if (Object.keys(hdrs).length > 0) {
    event.extra.http_response_headers = hdrs
  }

  const finger = ['oazapfts-http', String(ex.status)]
  const route = httpRouteOf(ex.headers)
  if (route) {
    finger.push(route)
    event.tags = { ...event.tags, http_route: route }
  }
  if (ex.data && typeof ex.data === 'object' && 'code' in ex.data) {
    const code = (ex.data as { code?: unknown }).code
    if (typeof code === 'string' || typeof code === 'number') {
      finger.push(String(code))
    }
  }
  event.fingerprint = finger
}

/** 401 : session expirée ou refusée, que l'app traite déjà. 403 : refus de droits ou de l'Assistant, attendus. */
const EXPECTED_HTTP_STATUSES = new Set([401, 403])

const STALE_CHUNK_MESSAGE_RE =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

function exceptionMessage(event: ErrorEvent, hint: EventHint): string {
  const ex = hint.originalException
  if (ex instanceof Error) return ex.message
  return event.exception?.values?.[0]?.value ?? ''
}

/**
 * Script injecté dans la page (extension, outil de supervision du poste) : ses frames n'ont pas de
 * fichier, mais une ligne, à la différence des frames natives (`Array.sort`, `Generator.next`).
 */
function comesFromInjectedScript(event: ErrorEvent): boolean {
  return (event.exception?.values ?? []).some((value) =>
    (value.stacktrace?.frames ?? []).some(
      (frame) => frame.filename === '<anonymous>' && frame.lineno != null
    )
  )
}

export function shouldDropEvent(event: ErrorEvent, hint: EventHint): boolean {
  const ex = hint.originalException
  if (isLikelyOazapftsHttpError(ex) && EXPECTED_HTTP_STATUSES.has(ex.status)) {
    return true
  }
  if (
    isStaleChunkReloadPending() &&
    STALE_CHUNK_MESSAGE_RE.test(exceptionMessage(event, hint))
  ) {
    return true
  }
  return comesFromInjectedScript(event)
}

export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint
): ErrorEvent | null {
  if (shouldDropEvent(event, hint)) return null
  applyHttpErrorContextToEvent(event, hint)
  return event
}
